import { useEffect, type ReactNode } from "react";
import { usePortalRole, type PortalRole } from "@/hooks/usePortalRole";
import LoadingScreen from "@/components/LoadingScreen";
import { useNavigate } from "react-router-dom";

interface RoleGateProps {
  /** Roles permitted to view children. */
  allowed: PortalRole[];
  children: ReactNode;
}

/**
 * Lightweight route-level role guard.
 * Renders children only when the active role is in `allowed`.
 * Shows a loading screen while the role is resolving, and an
 * unauthorised screen if the role doesn't match.
 */
export default function RoleGate({ allowed, children }: RoleGateProps) {
  const { role, roles, loading, setActiveRole } = usePortalRole();

  const hasActiveAccess = Boolean(role && allowed.includes(role));
  const fallbackRole =
    allowed.find((candidate) => candidate !== null && candidate !== "admin" && roles.includes(candidate)) ??
    allowed.find((candidate) => candidate !== null && roles.includes(candidate)) ??
    null;

  useEffect(() => {
    if (loading || hasActiveAccess || !fallbackRole) return;
    setActiveRole(fallbackRole);
  }, [fallbackRole, hasActiveAccess, loading, setActiveRole]);

  if (loading) return <LoadingScreen />;

  if (hasActiveAccess) {
    return <>{children}</>;
  }

  if (fallbackRole) {
    return <LoadingScreen />;
  }

  if (!role || !allowed.includes(role)) {
    return <RoleGateUnauthorised />;
  }

  return <>{children}</>;
}

function RoleGateUnauthorised() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md text-center space-y-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-foreground">Access Restricted</h1>
        <p className="text-muted-foreground">
          You don't have the right permissions to view this area.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate("/portal", { replace: true })}
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
